import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter, createRootRoute, createRoute } from '@tanstack/react-router'
import { Toaster } from 'sonner'
import { AppShell } from './components/layout/app-shell'
import { DashboardPage } from './routes/dashboard/index'
import { LoginPage } from './routes/login'
import { LoginMfaSetupPage } from './routes/login-mfa-setup'
import { AcceptInvitePage } from './routes/accept-invite'
import { M365TenantsPage } from './routes/m365-tenants/index'
import { M365TenantDetailPage } from './routes/m365-tenants/$id'
import { UsersPage } from './routes/users/index'
import { SubscriptionsPage } from './routes/subscriptions/index'
import { RecommendationsPage } from './routes/recommendations/index'
import { HistoryPage } from './routes/history/index'
import { ReportsPage } from './routes/reports/index'
import { ProfilePage } from './routes/profile/index'
import { SettingsLayout } from './routes/settings/index'
import { SettingsGeneralPage } from './routes/settings/general'
import { SettingsTeamPage } from './routes/settings/team'
import { SettingsLicensesPage } from './routes/settings/licenses'
import { SettingsRulesPage } from './routes/settings/rules'
import { SettingsEmailPage } from './routes/settings/email'
import { SyncJobsPage } from './routes/settings/sync-jobs'
import { AdminPage } from './routes/admin/index'
import { AdminClientsPage } from './routes/admin/clients'
import { AdminClientDetailPage } from './routes/admin/client-detail'
import { AdminClientLicensesPage } from './routes/admin/client-licenses'
import { AdminClientRecommendationRulesPage } from './routes/admin/client-recommendation-rules'
import { AdminM365TenantsPage } from './routes/admin/m365-tenants'
import { AdminUsersPage } from './routes/admin/users'
import { AdminAuditPage } from './routes/admin/audit'
import { AiRecommendationsPage } from './routes/ai-recommendations/index'
import { AdminAiDashboardPage } from './routes/admin/ai/index'
import { AdminAiTenantsPage } from './routes/admin/ai/tenants'
import { AdminAiContextsPage } from './routes/admin/ai/contexts'
import { AdminAiModerationPage } from './routes/admin/ai/moderation'
import { AdminAiRunsPage } from './routes/admin/ai/runs'
import { AdminAiRecommendationsPage } from './routes/admin/ai/recommendations'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000, retry: 1 } },
})

const rootRoute = createRootRoute()
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage })
const loginMfaSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/mfa-setup',
  component: LoginMfaSetupPage,
})
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: AcceptInvitePage,
})
const layoutRoute = createRoute({ getParentRoute: () => rootRoute, id: 'layout', component: AppShell })

const routes = [
  createRoute({ getParentRoute: () => layoutRoute, path: '/', component: DashboardPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/m365-tenants', component: M365TenantsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/m365-tenants/$id', component: M365TenantDetailPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/users', component: UsersPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/subscriptions', component: SubscriptionsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/recommendations', component: RecommendationsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/ai-recommendations', component: AiRecommendationsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/history', component: HistoryPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/reports', component: ReportsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/sync-jobs', component: SyncJobsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/profile', component: ProfilePage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin', component: AdminPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/clients', component: AdminClientsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/clients/$clientId', component: AdminClientDetailPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/clients/$clientId/recommendations', component: AdminClientRecommendationRulesPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/clients/$clientId/licenses', component: AdminClientLicensesPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/m365-tenants', component: AdminM365TenantsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/users', component: AdminUsersPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/audit', component: AdminAuditPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/ai', component: AdminAiDashboardPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/ai/tenants', component: AdminAiTenantsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/ai/contexts', component: AdminAiContextsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/ai/moderation', component: AdminAiModerationPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/ai/runs', component: AdminAiRunsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/admin/ai/recommendations', component: AdminAiRecommendationsPage }),
]

const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: SettingsLayout,
})
const settingsChildren = [
  createRoute({ getParentRoute: () => settingsRoute, path: '/', component: SettingsGeneralPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: '/team', component: SettingsTeamPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: '/licenses', component: SettingsLicensesPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: '/rules', component: SettingsRulesPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: '/email', component: SettingsEmailPage }),
]
const settingsRouteTree = settingsRoute.addChildren(settingsChildren)

const routeTree = rootRoute.addChildren([
  loginRoute,
  loginMfaSetupRoute,
  acceptInviteRoute,
  layoutRoute.addChildren([...routes, settingsRouteTree]),
])
const router = createRouter({ routeTree })

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster position="top-right" richColors />
    </QueryClientProvider>
  )
}
